import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Form } from 'react-bootstrap'

import { DashboardLayout } from '../../Components/Layout/DashboardLayout'
import CustomButton from '../../Components/CustomButton'
import CustomInput from '../../Components/CustomInput'
import { useCreateNeighborhoodMutation } from '../../Redux/Apis/Neighborhood'

import './style.css'

const CreateNeighborhood = () => {
  const navigate = useNavigate()
  const [createNeighborhood, { isLoading }] = useCreateNeighborhoodMutation()

  const allSections = ['Clubhouse', 'Caddyshack', '19th Hole', 'The Pros', 'Rangefinder', 'The Barn', 'Calcutta']

  const [formData, setFormData] = useState({
    slug: '',
    name: '',
    displayName: '',
    domain: '',
    apiUrl: '',
    branding: {
      primaryColor: '#193631',
      logo: '',
      favicon: ''
    },
    features: {
      sections: allSections,
      enableForums: true,
      enableDirections: true,
      enableChildTracking: true
    },
    contact: {
      supportEmail: '',
      phone: '',
      address: ''
    },
    mobileApps: {
      ios: {
        bundleId: '',
        appStoreId: ''
      },
      android: {
        packageName: '',
        playStoreId: ''
      }
    }
  })

  const handleChange = (field, value) => {
    if (field.includes('.')) {
      const keys = field.split('.')
      setFormData(prev => {
        const updated = { ...prev }
        let current = updated
        for (let i = 0; i < keys.length - 1; i++) {
          current = current[keys[i]]
        }
        current[keys[keys.length - 1]] = value
        return updated
      })
    } else {
      setFormData(prev => ({ ...prev, [field]: value }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    try {
      await createNeighborhood(formData).unwrap()
      toast.success('Neighborhood created successfully')
      navigate('/neighborhoods')
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to create neighborhood')
    }
  }

  return (
    <DashboardLayout>
      <div className="container-fluid">
        <div className="row mb-3">
          <div className="col-12">
            <div className="dashCard create-neighborhood-container">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="mainTitle">Create New Neighborhood</h2>
                <CustomButton
                  variant="secondary"
                  onClick={() => navigate('/neighborhoods')}
                >
                  Back to List
                </CustomButton>
              </div>

              <Form onSubmit={handleSubmit}>
                <div className="row">
                  {/* Basic Information */}
                  <div className="col-12">
                    <div className="section-divider">
                      <h5 className="section-title">Basic Information</h5>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <CustomInput
                      label="Slug"
                      id="slug"
                      type="text"
                      placeholder="the-landings"
                      value={formData.slug}
                      onChange={(e) => handleChange('slug', e.target.value)}
                      required
                    />
                    <small className="text-muted">URL-friendly identifier (lowercase, hyphens only)</small>
                  </div>

                  <div className="col-md-6">
                    <CustomInput
                      label="Name"
                      id="name"
                      type="text"
                      placeholder="The Landings"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <CustomInput
                      label="Display Name"
                      id="displayName"
                      type="text"
                      placeholder="The Turn - Landings"
                      value={formData.displayName}
                      onChange={(e) => handleChange('displayName', e.target.value)}
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <CustomInput
                      label="Domain"
                      id="domain"
                      type="text"
                      placeholder="landings.theturn.com"
                      value={formData.domain}
                      onChange={(e) => handleChange('domain', e.target.value)}
                      required
                    />
                  </div>

                  <div className="col-md-12">
                    <CustomInput
                      label="API URL"
                      id="apiUrl"
                      type="text"
                      placeholder="https://api.theturn.com"
                      value={formData.apiUrl}
                      onChange={(e) => handleChange('apiUrl', e.target.value)}
                      required
                    />
                  </div>

                  {/* Branding */}
                  <div className="col-12">
                    <div className="section-divider">
                      <h5 className="section-title">Branding</h5>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <label className="mainLabel">Primary Color</label>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="color"
                        className="form-control form-control-color"
                        value={formData.branding.primaryColor}
                        onChange={(e) => handleChange('branding.primaryColor', e.target.value)}
                        style={{ width: '60px', height: '40px', flexShrink: 0 }}
                      />
                      <input
                        type="text"
                        className="mainInput flex-grow-1"
                        value={formData.branding.primaryColor}
                        onChange={(e) => handleChange('branding.primaryColor', e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <CustomInput
                      label="Logo URL"
                      id="logo"
                      type="text"
                      placeholder="/assets/logo.png"
                      value={formData.branding.logo}
                      onChange={(e) => handleChange('branding.logo', e.target.value)}
                    />
                  </div>

                  <div className="col-md-6">
                    <CustomInput
                      label="Favicon URL"
                      id="favicon"
                      type="text"
                      placeholder="/assets/favicon.png"
                      value={formData.branding.favicon}
                      onChange={(e) => handleChange('branding.favicon', e.target.value)}
                    />
                  </div>

                  {/* Mobile Apps */}
                  <div className="col-12">
                    <div className="section-divider">
                      <h5 className="section-title">Mobile Apps</h5>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <CustomInput
                      label="iOS Bundle ID"
                      id="iosBundleId"
                      type="text"
                      placeholder="com.theturn.landings"
                      value={formData.mobileApps.ios.bundleId}
                      onChange={(e) => handleChange('mobileApps.ios.bundleId', e.target.value)}
                    />
                  </div>

                  <div className="col-md-6">
                    <CustomInput
                      label="Android Package Name"
                      id="androidPackage"
                      type="text"
                      placeholder="com.theturn.landings"
                      value={formData.mobileApps.android.packageName}
                      onChange={(e) => handleChange('mobileApps.android.packageName', e.target.value)}
                    />
                  </div>

                  {/* Contact */}
                  <div className="col-12">
                    <div className="section-divider">
                      <h5 className="section-title">Contact Information</h5>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <CustomInput
                      label="Support Email"
                      id="supportEmail"
                      type="email"
                      placeholder="support@landings.theturn.com"
                      value={formData.contact.supportEmail}
                      onChange={(e) => handleChange('contact.supportEmail', e.target.value)}
                    />
                  </div>

                  <div className="col-md-6">
                    <CustomInput
                      label="Phone"
                      id="phone"
                      type="text"
                      placeholder="+1234567890"
                      value={formData.contact.phone}
                      onChange={(e) => handleChange('contact.phone', e.target.value)}
                    />
                  </div>

                  <div className="col-md-6">
                    <CustomInput
                      label="Address"
                      id="address"
                      type="text"
                      placeholder="123 Main St"
                      value={formData.contact.address}
                      onChange={(e) => handleChange('contact.address', e.target.value)}
                    />
                  </div>

                  {/* Features */}
                  <div className="col-12">
                    <div className="section-divider">
                      <h5 className="section-title">Features</h5>
                    </div>
                  </div>

                  <div className="col-md-12 mb-3">
                    <div className="features-container">
                      <div className="feature-item">
                        <Form.Check
                          type="checkbox"
                          id="create-feature-forums"
                          label="Enable Forums (19th Hole)"
                          checked={formData.features.enableForums}
                          onChange={(e) => handleChange('features.enableForums', e.target.checked)}
                        />
                        <small className="text-muted d-block ms-4">Allow users to create posts and discussions</small>
                      </div>
                      <div className="feature-item">
                        <Form.Check
                          type="checkbox"
                          id="create-feature-directions"
                          label="Enable Directions (Rangefinder)"
                          checked={formData.features.enableDirections}
                          onChange={(e) => handleChange('features.enableDirections', e.target.checked)}
                        />
                        <small className="text-muted d-block ms-4">Allow users to save and share directions</small>
                      </div>
                      <div className="feature-item">
                        <Form.Check
                          type="checkbox"
                          id="create-feature-tracking"
                          label="Enable Child Tracking"
                          checked={formData.features.enableChildTracking}
                          onChange={(e) => handleChange('features.enableChildTracking', e.target.checked)}
                        />
                        <small className="text-muted d-block ms-4">Allow parents to track children's locations</small>
                      </div>
                    </div>
                  </div>

                  {/* Submit */}
                  <div className="col-12 mt-4">
                    <div className="d-flex gap-3">
                      <CustomButton
                        type="submit"
                        variant="primary"
                        disabled={isLoading}
                      >
                        {isLoading ? 'Creating...' : 'Create Neighborhood'}
                      </CustomButton>
                      <CustomButton
                        variant="secondary"
                        onClick={() => navigate('/neighborhoods')}
                      >
                        Cancel
                      </CustomButton>
                    </div>
                  </div>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}

export default CreateNeighborhood

